/* eslint-disable consistent-return */
import { block, clear, fetch, limitAmount, setCart, update } from './helpers';
import { get } from 'lodash';
import axios from 'axios';

import {
    SET_CART_DRAGGING_TUTORIAL,
    SET_CART_ERROR,
    SET_CART_FOOTER_FIXED,
    SET_CART_TEMP_ITEM,
    SET_CART_TOOLTIP_ID,
} from 'reducers/actions-types';
import { dispatchError } from 'reducers/helpers';
import { copyOrder } from 'constants/api-urls';
import { CONFIRMED } from 'constants/order-states';
import { MISSING_ZIP_CODE, ORDER_PENDING, PRODUCT_NOT_IN_REGION } from 'constants/cart-error-types';
import { RESET_CART } from '../../actions-types';

const availableCartErrorTypes = [MISSING_ZIP_CODE, ORDER_PENDING, PRODUCT_NOT_IN_REGION];

const FOOTER_HEIGHT_INLINE = 134;

/**
 * Users can deal with the cart only if they have set a valid zip code
 * @returns {boolean}
 */
function isValid(dispatch, getState, product) {
    const state = getState();
    const activeRegion = state.region.active;
    const hasZip = !!activeRegion;
    const productCooperative = product.cooperative || state.router.cooperative;
    const isInRegion = !productCooperative || activeRegion && activeRegion.cooperative === productCooperative;
    const hasOrdersLookingForAPicker = state.cart.state === CONFIRMED;
    const hasErrors = !hasZip || hasOrdersLookingForAPicker || !isInRegion;

    // dispatch the cart error
    if (hasErrors) {
        // block the user in case the zip was not defined
        if (!hasZip) {
            dispatch(setCartError({ error: MISSING_ZIP_CODE }));
            dispatch({ type: SET_CART_TEMP_ITEM, tempItem: product });
            // advise the user that he/she the product is not available in his/her region
        } else if (!isInRegion) {
            dispatch(setCartError({ error: PRODUCT_NOT_IN_REGION }));
        } else if (hasOrdersLookingForAPicker) {
            // block the user because of pending orders
            dispatch(setCartError({ error: ORDER_PENDING }));
        }
    }

    return !hasErrors;
}

/* eslint-disable camelcase */
/**
 * Add one unit of a product to the cart
 * @param product
 * @returns {function(*, *)}
 */
function addProduct({ product }) {
    return (dispatch, getState) => {
        const id = product.id;
        const currentState = getState();

        const existingProduct = currentState.cart.items[id];

        if (currentState.cart.tempItem) {
            // reset the temp item if it was set before
            dispatch({ type: SET_CART_TEMP_ITEM, tempItem: null });
        }

        if (!isValid(dispatch, getState, product)) {
            return;
        }

        let amount = existingProduct ? existingProduct.amount + 1 : 1;
        amount = limitAmount(amount);

        const data = {
            product_id: id,
            amount,
        };

        return update({ data, dispatch, product, currentState }, getState);
    };
}

/**
 * Change the amount of a product in the cart
 * @param product
 * @param amount
 * @returns {function(*, *)}
 */
function setProductAmount({ product, amount } = {}) {
    return (dispatch, getState) => {
        if (!isValid(dispatch, getState, product)) {
            return;
        }

        // Limit amount to at least 0
        amount = limitAmount(amount);

        const { id } = product;

        const data = {
            product_id: id,
            amount,
        };

        return update({ data, dispatch, product, currentState: getState() }, getState);
    };
}

/**
 * Fetch the cart
 * @returns {function(*)}
 */
function fetchCart() {
    return (dispatch, getState) => {
        return fetch({ dispatch }, getState);
    };
}

/**
 * Empty the cart
 * @returns {function(*)}
 */
function clearCart() {
    return (dispatch, getState) => {
        if (getState().cart.isEmpty) {
            return Promise.resolve();
        }

        return clear({ dispatch });
    };
}

/**
 * Reset the cart
 * @returns {object}
 */
function reset() {
    return { type: RESET_CART };
}

/**
 * Declare on which item there currently is a tooltip shown
 * This is needed to work around the stacking context, unfortunately
 * @returns {function(*)}
 */
function setTooltipId({ tooltipId }) {
    return { type: SET_CART_TOOLTIP_ID, tooltipId };
}

/**
 * Set the cart error type
 * @param {string} cartError - cart error type
 * @returns {object} - redux event
 */
function setCartError({ error }) {
    if (error && !availableCartErrorTypes.includes(error)) {
        throw new Error(`The cart error "${ error }" is not supported. Please use one of the following errors: ${ availableCartErrorTypes.join(',') } `);
    }

    return { type: SET_CART_ERROR, error };
}

/**
 * Clear the cart errors
 * @returns {object} - redux event
 */
function clearCartError() {
    return setCartError({ error: null });
}

/**
 * Set the mobile dragging tutorial either to true or false
 * @param {boolean} hideDraggingTutorial
 * @returns {{type: string, hideDraggingTutorial: boolean}}
 */
function setDraggingTutorial({ hideDraggingTutorial }) {
    return { type: SET_CART_DRAGGING_TUTORIAL, hideDraggingTutorial };
}

/**
 * Set the footer state
 * @param isFooterFixed
 * @return {{type: *, isFooterFixed: *}}
 */
function setFooterState(isFooterFixed) {
    return { type: SET_CART_FOOTER_FIXED, isFooterFixed };
}

/**
 * Set state of footer wether it's fixed or not
 * @param {Element} child
 */
function updateFooterState(child) {
    return function(dispatch, getState) {
        const { viewport, cart } = getState();
        const { height } = viewport;
        const viewportMqQuery = viewport.mq.query;
        const footerFixed = viewportMqQuery === 'xs' || child.scrollHeight + FOOTER_HEIGHT_INLINE > height;

        if (footerFixed !== cart.isFooterFixed) {
            dispatch(setFooterState(footerFixed));
        }
    };
}

/**
 * Copy an order to the current cart
 * @param {string} orderId
 * @return {Function}
 */
function copyFromOrder(orderId) {
    return function(dispatch, getState) {
        return axios.post(copyOrder(orderId))
            .then((response) => {
                /** @var {ICart} */
                const cart = response.data;
                setCart(dispatch, getState, cart);
                return cart;
            })
            .catch(e => {
                const transKey = get(e, 'data.error.trans_key');
                if (transKey) {
                    throw new Error(transKey);
                }
                dispatchError(dispatch, e, {
                    url: copyOrder(orderId),
                    description: e,
                });
                return null;
            });
    };
}

export default Object.freeze({
    addProduct,
    copyFromOrder,
    setCartError,
    clearCartError,
    setProductAmount,
    fetchCart,
    clearCart,
    reset,
    block,
    setDraggingTutorial,
    setTooltipId,
    updateFooterState,
});

/* eslint-enable camelcase */



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/cart/actions.js